import React, { FC, Fragment, useState } from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import Auth, { CognitoUser } from '@aws-amplify/auth';

import {
  ClientLogo,
  Layouts,
  Loaders,
  PasswordRequirements,
  ValidatedTextFieldNext,
  VisibilityAdornment,
} from 'components';
import { useGlobalClient, useNotify } from 'hooks';
import { getAppImage } from 'helpers';

interface Props {
  cognitoUser: CognitoUser;
}

const RequireNewPassword: FC<Props> = ({ cognitoUser }) => {
  const [working, setWorking] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const client = useGlobalClient();
  const { t } = useTranslation();
  const notify = useNotify();

  const initialValues = {
    password: '',
  };

  async function handleSubmit(values: typeof initialValues) {
    setWorking(true);
    try {
      await Auth.completeNewPassword(cognitoUser, values.password);
    } catch (error) {
      setWorking(false);
      notify.error({ message: error });
    }
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/boat.png', 500)} />;
  }

  function renderMain() {
    return (
      <Fragment>
        <Formik onSubmit={handleSubmit} initialValues={initialValues}>
          <Layouts.OnboardingBox component={Form}>
            <Box textAlign="center">
              <ClientLogo client={client} />
            </Box>
            <Typography align="center" paragraph variant="h2">
              {t('auth:forgotPasswordSidebarHeader')}
            </Typography>
            <Typography align="center" paragraph>
              {t('auth:requirePasswordChange')}
            </Typography>
            <Box paddingTop={3}>
              <ValidatedTextFieldNext
                enableTrim
                fullWidth
                name="password"
                type={passwordVisible ? 'text' : 'password'}
                label={t('auth:forgotPasswordNewPassword')}
                placeholder={t('auth:forgotPasswordNewPassword')}
                InputProps={{
                  endAdornment: (
                    <VisibilityAdornment
                      visible={passwordVisible}
                      onToggle={() => setPasswordVisible(!passwordVisible)}
                    />
                  ),
                }}
              />
              <Box marginTop={2}>
                <PasswordRequirements name="password" />
              </Box>
            </Box>
            <Box paddingTop={3}>
              <Button
                fullWidth
                type="submit"
                size="large"
                variant="contained"
                color="primary"
                disabled={working}
              >
                {working ? <Loaders.Standard height={26} /> : t('common:save')}
              </Button>
            </Box>
          </Layouts.OnboardingBox>
        </Formik>
      </Fragment>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default RequireNewPassword;
